import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { 
  BookOpen, ChevronDown, School, ArrowRight, 
  Calendar, CheckCircle2, GraduationCap 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen'; // <--- IMPORT STANDARD

const CourseDetails = () => {
  const { deptId, courseId } = useParams();
  
  // Stati Dati
  const [course, setCourse] = useState(null);
  const [dept, setDept] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stati Filtri
  const [selectedYear, setSelectedYear] = useState(1);
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [availableCurricula, setAvailableCurricula] = useState([]);
  
  // Stati UI
  const [isElectivesOpen, setIsElectivesOpen] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Corso e Dipartimento
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*, departments(acronym, name)')
        .eq('id', courseId)
        .single();

      if (courseError) throw courseError;
      setCourse(courseData);
      setDept(courseData.departments);

      // 2. Fetch Materie
      const { data: subjectsData, error: subjError } = await supabase
        .from('subjects')
        .select('*')
        .eq('course_id', courseId)
        .order('name');

      if (subjError) throw subjError;
      setSubjects(subjectsData || []);

      // 3. Estrai Curricula Unici
      if (courseData.has_curricula) {
        // Crea un Set di nomi di curriculum unici (escludendo i null)
        const curricula = [...new Set(subjectsData.map(s => s.curriculum).filter(Boolean))];
        setAvailableCurricula(curricula);
        // Seleziona il primo di default
        if (curricula.length > 0) setSelectedCurriculum(curricula[0]);
      }

    } catch (error) {
      console.error("Errore fetch corso:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- LOGICA DI FILTRAGGIO ---

  // 1. MATERIE OBBLIGATORIE: Dipendono dall'ANNO e dal CURRICULUM
  const mandatory = useMemo(() => {
    if (!subjects) return [];
    return subjects.filter(s => {
      // Escludi le materie a scelta
      if (s.is_elective) return false;
      
      // Filtra per ANNO (Fondamentale per le obbligatorie)
      if (s.year !== selectedYear) return false;

      // Filtra per Curriculum (se esiste)
      if (course?.has_curricula && selectedCurriculum) {
         return s.curriculum === null || s.curriculum === selectedCurriculum;
      }
      return true;
    });
  }, [subjects, selectedYear, selectedCurriculum, course]);

  // 2. MATERIE A SCELTA: Dipendono SOLO dal CURRICULUM (Visibili in tutti gli anni)
  const electives = useMemo(() => {
    if (!subjects) return [];
    return subjects.filter(s => {
      // Includi SOLO le materie a scelta
      if (!s.is_elective) return false;

      // NON FILTRIAMO PER ANNO QUI! (Così si vedono sempre)

      // Filtra per Curriculum (se esiste)
      if (course?.has_curricula && selectedCurriculum) {
         return s.curriculum === null || s.curriculum === selectedCurriculum;
      }
      return true;
    });
  }, [subjects, selectedCurriculum, course]); 


  // Calcola anni disponibili (es. [1, 2, 3]) basandosi sulle materie obbligatorie
  const availableYears = useMemo(() => {
    const mandatorySubs = subjects.filter(s => !s.is_elective);
    const years = [...new Set(mandatorySubs.map(s => s.year))].sort((a,b) => a-b);
    return years.length > 0 ? years : [1, 2, 3]; 
  }, [subjects]);


  // --- LOADING SCREEN STANDARD ---
  if (loading) return <LoadingScreen />;
  
  if (!course) return <div className="p-20 text-center text-slate-500 font-bold">Corso non trovato o errore di connessione.</div>;

  return (
    <div className="relative min-h-screen bg-slate-50 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title={`${course.name} - Piano di Studi`} description={`Materie e programma per ${course.name}.`} url={`/course/${deptId}/${courseId}`} />
      
      {/* Sfondo Animato */}
      <div className="absolute top-0 left-[-10%] w-[500px] h-[500px] bg-indigo-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader 
            title={course.name} 
            subtitle="Piano di studi e materiale didattico." 
            breadcrumbs={[
                {label: 'Dip.', link: '/dipartimenti'}, 
                {label: dept?.acronym || 'Corso', link: `/dept/${deptId}`}, 
                {label: 'Piano di Studi'}
            ]} 
        />

        {/* --- SELETTORE CURRICULUM (Solo se presenti) --- */}
        {course.has_curricula && availableCurricula.length > 0 && (
            <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 ml-2">Seleziona Curriculum</p>
                <div className="flex flex-wrap gap-3">
                    {availableCurricula.map((curr) => (
                        <button 
                            key={curr} 
                            onClick={() => setSelectedCurriculum(curr)} 
                            className={`px-6 py-3 rounded-2xl text-sm font-bold border transition-all shadow-sm flex items-center gap-2 ${
                                selectedCurriculum === curr 
                                ? 'bg-slate-900 border-slate-900 text-white shadow-lg scale-105' 
                                : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                            }`}
                        >
                            {selectedCurriculum === curr && <CheckCircle2 size={16} className="text-green-400"/>}
                            {curr}
                        </button>
                    ))}
                </div>
            </div>
        )}

        {/* --- SELETTORE ANNO --- */}
        <div className="flex border-b-2 border-slate-100 mb-10 overflow-x-auto no-scrollbar">
            {availableYears.map(year => (
                <button 
                    key={year} 
                    onClick={() => setSelectedYear(year)} 
                    className={`px-8 py-4 text-sm font-bold border-b-2 transition-all -mb-[2px] whitespace-nowrap flex items-center gap-2 ${
                        selectedYear === year 
                        ? 'border-indigo-600 text-indigo-600' 
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                >
                    <Calendar size={16}/> {year}° Anno
                </button>
            ))}
        </div>

        {/* --- LISTA MATERIE OBBLIGATORIE (GRIGLIA) --- */}
        <div className="mb-12">
            <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                <BookOpen className="text-indigo-600"/> Materie Obbligatorie
            </h3>
            
            {mandatory.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {mandatory.map((subject) => (
                        <Link 
                            key={subject.id} 
                            to={`/subject/${deptId}/${courseId}/${encodeURIComponent(subject.name)}`} 
                            className="group bg-white border border-slate-100 p-6 rounded-[2rem] hover:border-indigo-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                            
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold shadow-sm group-hover:scale-110 transition-transform">
                                        <School size={22} />
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Materia</span>
                                        <h3 className="font-bold text-slate-800 text-lg group-hover:text-indigo-700 transition-colors leading-tight">
                                            {subject.name}
                                        </h3>
                                    </div>
                                </div>
                                <ArrowRight size={20} className="text-slate-300 group-hover:text-indigo-500 opacity-0 group-hover:opacity-100 transition-all transform -translate-x-4 group-hover:translate-x-0" />
                            </div>
                        </Link>
                    ))}
                </div>
            ) : (
                <div className="text-slate-400 italic bg-slate-50 p-6 rounded-2xl border border-dashed border-slate-200">
                    Nessuna materia obbligatoria trovata per questo anno.
                </div>
            )}
        </div>

        {/* --- ESAMI A SCELTA (ACCORDION - VISIBILI SEMPRE) --- */}
        <div className="bg-white border border-slate-100 rounded-[2rem] overflow-hidden shadow-sm hover:shadow-md transition">
            <button 
                onClick={() => setIsElectivesOpen(!isElectivesOpen)} 
                className="w-full flex items-center justify-between p-8 hover:bg-slate-50 transition outline-none"
            >
                <div className="flex items-center gap-4">
                    <div className="bg-yellow-100 text-yellow-600 p-3 rounded-2xl">
                        <GraduationCap size={24} />
                    </div>
                    <div className="text-left">
                        <h3 className="font-bold text-slate-900 text-lg">Esami a Scelta ({electives.length})</h3>
                        <p className="text-slate-500 text-xs">Materie opzionali (visibili per tutti gli anni)</p>
                    </div>
                </div>
                <div className={`w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center transition-all ${isElectivesOpen ? 'bg-slate-900 text-white rotate-180' : 'bg-white text-slate-400'}`}>
                    <ChevronDown size={20} />
                </div>
            </button>
            
            {isElectivesOpen && (
                <div className="p-8 border-t border-slate-100 bg-slate-50/50 animate-in slide-in-from-top-2">
                    <div className="grid md:grid-cols-3 gap-3">
                        {electives.length > 0 ? (
                            electives.map((item) => (
                                <Link 
                                    key={item.id} 
                                    to={`/subject/${deptId}/${courseId}/${encodeURIComponent(item.name)}`} 
                                    className="block p-4 rounded-2xl bg-white border border-slate-200 hover:border-yellow-400 hover:shadow-md text-slate-600 font-bold transition text-sm flex items-center justify-between group"
                                >
                                    {item.name}
                                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 text-yellow-500 transition"/>
                                </Link>
                            ))
                        ) : (
                            <p className="text-slate-500 italic text-sm col-span-full text-center py-4">
                                Nessuna materia a scelta trovata per questo curriculum.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};

export default CourseDetails;